/**
 * D6BattleWidget - Arène de combat épique 2D6
 * Design premium avec dés 3D intégrés
 */

import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Platform,
  Modal,
  ScrollView,
  Dimensions,
  PanResponder,
  Image,
} from 'react-native';
import {PlayerStep} from '../../../models/PlayerStep';
import {usePlayer} from '../../../contexts/PlayerContext';
import {useTheme} from '../../../contexts/ThemeContext';
import GameStateHelper from '../../../helpers/GameStateHelper';
import gameStateService from '../../../services/gameStateService';
import {Canvas} from '@react-three/fiber';
import {Physics, RigidBody, CuboidCollider} from '@react-three/rapier';
import * as THREE from 'three';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {
  faBolt,
  faSkull,
  faShield,
  faHandPointUp,
  faDice,
  faBalanceScale,
  faDumbbell,
  faHeart,
} from '@fortawesome/free-solid-svg-icons';
import Svg, {Path} from 'react-native-svg';

// Imports des fichiers découpés
import {CombatState, D6BattleWidgetProps} from './D6BattleWidget.types';
import {TopDownCamera, InvisibleTable, VisibleTableMat, PhysicsDice3D} from './D6BattleWidget.3d';
import {parseValue} from './D6BattleWidget.utils';
import {useComputeAttributeValues, useLoadCombatValues} from './D6BattleWidget.hooks';
import {getValueDice} from '../util/DiceUtil';
import {resetGlobalSeed, createSeedFromTimeAndForce, getGlobalSeededRandom} from '../util/SeededRandom';

const {width: screenWidth} = Dimensions.get('window');

const D6BattleWidgetComponent: React.FC<D6BattleWidgetProps> = ({step, onFlee}) => {
  const {gameState, setGameState, editorMode} = usePlayer();
  const {theme} = useTheme();
  const [combatState, setCombatState] = useState<CombatState>({
    phase: 'idle',
    playerDice1: null,
    playerDice2: null,
    enemyDice1: null,
    enemyDice2: null,
    playerTotal: 0,
    enemyTotal: 0,
  });

  // Valeurs de combat
  const [playerForce, setPlayerForce] = useState<number>(0);
  const [playerLife, setPlayerLife] = useState<number | null>(null);
  const [playerMaxLife, setPlayerMaxLife] = useState<number | null>(null);
  const [playerDamage, setPlayerDamage] = useState<number>(0);
  const [playerAttackBonus, setPlayerAttackBonus] = useState<number>(0);
  const [playerDamageBonus, setPlayerDamageBonus] = useState<number>(0);

  const [enemyForce, setEnemyForce] = useState<number>(0);
  const [enemyLife, setEnemyLife] = useState<number | null>(null);
  const [enemyMaxLife, setEnemyMaxLife] = useState<number | null>(null);
  const [enemyDamage, setEnemyDamage] = useState<number>(0);
  const [enemyIntention, setEnemyIntention] = useState<string>('Attaque');

  // Messages
  const [battleResultMessage, setBattleResultMessage] = useState<string | null>(null);
  const [showOutcomeMessage, setShowOutcomeMessage] = useState(false);
  const [defeatMessage, setDefeatMessage] = useState<string | null>(null);
  
  // Animation typewriter pour les calculs
  const [displayedPlayerText, setDisplayedPlayerText] = useState('');
  const [displayedEnemyText, setDisplayedEnemyText] = useState('');

  // État du lancer de dés (intégré, pas overlay)
  const [isDiceRolling, setIsDiceRolling] = useState(false);
  const [diceKey, setDiceKey] = useState(0); // Pour forcer le remontage du Canvas
  const swipeDataRef = useRef<{strength: number; dirX: number; dirZ: number} | null>(null);
  const [showRerollOverlay, setShowRerollOverlay] = useState(false);
  const [impulseKey, setImpulseKey] = useState(0); // Pour déclencher l'application d'impulsion
  // ⚠️ CRITIQUE : État pour tracker si on a déjà lancé au moins une fois
  // Permet de garder le Canvas visible même après réinitialisation en phase 'idle'
  const [hasEverRolled, setHasEverRolled] = useState(false);
  // ⚠️ CRITIQUE : Utiliser un état au lieu d'un ref pour que React détecte les changements
  const [diceImpulses, setDiceImpulses] = useState<{
    playerDice1?: {linear: [number, number, number]; angular: [number, number, number]};
    playerDice2?: {linear: [number, number, number]; angular: [number, number, number]};
    enemyDice1?: {linear: [number, number, number]; angular: [number, number, number]};
    enemyDice2?: {linear: [number, number, number]; angular: [number, number, number]};
  }>({});

  // Animations
  const shakeAnim = useRef(new Animated.Value(0)).current;
  const fadeInAnim = useRef(new Animated.Value(0)).current;
  const impactAnim = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const outcomeMessageAnim = useRef(new Animated.Value(0)).current; // Animation pour le message de résultat
  const rerollOverlayAnim = useRef(new Animated.Value(0)).current; // Animation pour l'overlay de relance

  // État du geste de swipe
  const [swipeForce, setSwipeForce] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState({x: 0, y: 0});

  // PanResponder pour le geste de swipe
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => combatState.phase === 'idle' || combatState.phase === 'results',
      onMoveShouldSetPanResponder: () => combatState.phase === 'idle' || combatState.phase === 'results',
      
      onPanResponderGrant: () => {
        console.log('🎮 [Swipe] Début du geste');
      },
      
      onPanResponderMove: (evt, gestureState) => {
        // Calculer la force du swipe (distance)
        const distance = Math.sqrt(gestureState.dx ** 2 + gestureState.dy ** 2);
        setSwipeForce(distance);
        
        // Direction normalisée
        if (distance > 0) {
          setSwipeDirection({
            x: gestureState.dx / distance,
            y: gestureState.dy / distance,
          });
        }
      },
      
      onPanResponderRelease: (evt, gestureState) => {
        const distance = Math.sqrt(gestureState.dx ** 2 + gestureState.dy ** 2);
        const velocity = Math.sqrt(gestureState.vx ** 2 + gestureState.vy ** 2);
        
        console.log('🎮 [Swipe] Fin du geste:', {distance, velocity});
        
        // Si swipe long (>50px) OU tap rapide (distance < 20px)
        if (distance > 50) {
          console.log('🎮 [Swipe] LANCER SWIPE DÉCLENCHÉ !');
          rollDiceWithSwipe(gestureState.dx, gestureState.dy, velocity);
        } else if (distance < 20) {
          console.log('🎮 [Swipe] TAP RAPIDE DÉTECTÉ, lancer simple !');
          rollDice();
        } else {
          console.log('🎮 [Swipe] Geste trop court, ignoré');
        }
        
        // Reset
        setSwipeForce(0);
        setSwipeDirection({x: 0, y: 0});
      },
    })
  ).current;

  // Animation d'entrée
  useEffect(() => {
    Animated.timing(fadeInAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();

    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.03,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, []);

  // Hooks personnalisés
  const computeAttributeValues = useComputeAttributeValues(gameState);
  const loadCombatValues = useLoadCombatValues(
    gameState,
    step,
    {
      setPlayerForce,
      setPlayerDamage,
      setPlayerLife,
      setPlayerMaxLife,
      setPlayerAttackBonus,
      setPlayerDamageBonus,
      setEnemyForce,
      setEnemyDamage,
      setEnemyLife,
      setEnemyMaxLife,
      setEnemyIntention,
      setDefeatMessage,
    },
    computeAttributeValues
  );

  // Charger les valeurs depuis le gameState quand il change
  // ⚠️ CRITIQUE : Toujours recharger depuis le gameState pour avoir les valeurs à jour après un combat
  useEffect(() => {
    if (gameState) {
      loadCombatValues();
    }
  }, [gameState, step, loadCombatValues]);


  const rollDiceWithSwipe = useCallback((dx: number, dy: number, velocity: number) => {
    console.log('🎮 [rollDiceWithSwipe] RELANCER AVEC GESTE !');
    console.log('🎮 [rollDiceWithSwipe] Delta:', {dx, dy}, 'Vitesse:', velocity);
    
    if (combatState.phase !== 'idle' && combatState.phase !== 'results') {
      console.log('🎮 [rollDiceWithSwipe] Bloqué, phase =', combatState.phase);
      return;
    }

    // Calculer la force du swipe basée sur la vélocité (0.5-3)
    const swipeStrength = Math.min(3, Math.max(0.5, velocity * 0.5));
    
    // ⚠️ CRITIQUE : Calculer le nouveau diceKey AVANT de calculer le seed pour garantir l'unicité
    const newKey = diceKey + 1;
    
    // ⚠️ CRITIQUE : Réinitialiser le seed avec le temps, la force du geste ET le diceKey
    const seed = createSeedFromTimeAndForce(swipeStrength, newKey);
    resetGlobalSeed(seed);
    console.log('🎲 [rollDiceWithSwipe] Seed réinitialisé:', seed, 'force:', swipeStrength, 'diceKey:', newKey);

    // Si on est en phase 'results', on relance depuis la position actuelle avec impulsion
    if (combatState.phase === 'results') {
      console.log('🎮 [rollDiceWithSwipe] RELANCE depuis phase results');
      
      // Animation de shake
      Animated.sequence([
        Animated.timing(shakeAnim, {toValue: 5, duration: 40, useNativeDriver: true}),
        Animated.timing(shakeAnim, {toValue: -5, duration: 40, useNativeDriver: true}),
        Animated.timing(shakeAnim, {toValue: 0, duration: 40, useNativeDriver: true}),
      ]).start();

      setBattleResultMessage(null);
      setShowOutcomeMessage(false);
      setShowRerollOverlay(false); // Masquer l'overlay
      outcomeMessageAnim.setValue(0);
      rerollOverlayAnim.setValue(0);
      setDisplayedPlayerText('');
      setDisplayedEnemyText('');
      
      // ⚠️ CRITIQUE : Réinitialiser TOUS les états pour la relance
      diceCompleteCalledRef.current = false;
      setSettledDiceResults({playerDice1: null, playerDice2: null, enemyDice1: null, enemyDice2: null});
      
      // ⚠️ CRITIQUE : S'assurer que isDiceRolling est true pour que la scène 3D reste montée
      setIsDiceRolling(true);
      
      // Direction du swipe normalisée
      const distance = Math.sqrt(dx ** 2 + dy ** 2);
      const dirX = distance > 0 ? (dx / distance) : 0;
      const dirZ = distance > 0 ? (-dy / distance) : 0;
      
      // Calculer les impulsions pour chaque dé - optimisées pour dés plus petits
      const baseVelMultiplier = swipeStrength * 1.3; // Plus d'énergie pour rotation fluide
      const swipeInfluenceX = dirX * 30; // Direction X du swipe
      const swipeInfluenceZ = dirZ * 30; // Direction Z du swipe
      const baseVerticalVel = -4.0 - (swipeStrength * 1.2); // Vélocité verticale optimisée
      
      // Utiliser le seeded random pour des résultats cohérents mais variés
      const rng = getGlobalSeededRandom();
      
      const newImpulses = {
        playerDice1: {
          linear: [((rng.random() - 0.5) * 3 + swipeInfluenceX * 0.7) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 3 + swipeInfluenceZ * 0.7) * baseVelMultiplier] as [number, number, number],
          angular: [(rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier] as [number, number, number],
        },
        playerDice2: {
          linear: [((rng.random() - 0.5) * 3 + swipeInfluenceX * 0.7) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 3 + swipeInfluenceZ * 0.7) * baseVelMultiplier] as [number, number, number],
          angular: [(rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier] as [number, number, number],
        },
        enemyDice1: {
          linear: [((rng.random() - 0.5) * 3 + swipeInfluenceX * 0.7) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 3 + swipeInfluenceZ * 0.7) * baseVelMultiplier] as [number, number, number],
          angular: [(rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier] as [number, number, number],
        },
        enemyDice2: {
          linear: [((rng.random() - 0.5) * 3 + swipeInfluenceX * 0.7) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 3 + swipeInfluenceZ * 0.7) * baseVelMultiplier] as [number, number, number],
          angular: [(rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier, (rng.random() - 0.5) * 40 * baseVelMultiplier] as [number, number, number],
        },
      };
      
      // ⚠️ CRITIQUE : Réinitialiser l'état de combat AVANT de déclencher les impulsions
      setCombatState((prev) => ({
        ...prev,
        phase: 'rolling',
        playerDice1: null,
        playerDice2: null,
        enemyDice1: null,
        enemyDice2: null,
        playerTotal: 0,
        enemyTotal: 0,
      }));
      
      // ⚠️ CRITIQUE : Mettre à jour les impulsions dans l'état pour que React détecte le changement
      setDiceImpulses(newImpulses);
      
      // Déclencher l'application des impulsions
      setImpulseKey(prev => prev + 1);
      console.log('🎮 [rollDiceWithSwipe] Impulsions mises à jour et déclenchées');
      
      console.log('🎮 [rollDiceWithSwipe] Impulsions calculées, application dans 10ms');
      return;
    }

    // Si première fois (phase 'idle'), créer les dés normalement
    // Animation de shake
    Animated.sequence([
      Animated.timing(shakeAnim, {toValue: 5, duration: 40, useNativeDriver: true}),
      Animated.timing(shakeAnim, {toValue: -5, duration: 40, useNativeDriver: true}),
      Animated.timing(shakeAnim, {toValue: 0, duration: 40, useNativeDriver: true}),
    ]).start();

    setBattleResultMessage(null);
    setShowOutcomeMessage(false);
    
    diceCompleteCalledRef.current = false;
    setSettledDiceResults({playerDice1: null, playerDice2: null, enemyDice1: null, enemyDice2: null});
    
    // swipeStrength est déjà calculé au début de la fonction
    console.log('🎮 [rollDiceWithSwipe] Force multiplieur:', swipeStrength);
    
    // Direction du swipe normalisée
    const distance = Math.sqrt(dx ** 2 + dy ** 2);
    const dirX = distance > 0 ? (dx / distance) : 0;
    const dirZ = distance > 0 ? (-dy / distance) : 0;
    
    // Stocker la force/direction du swipe dans un ref
    swipeDataRef.current = {
      strength: swipeStrength,
      dirX,
      dirZ,
    };
    
    // Réinitialiser complètement pour forcer le remontage du Canvas
    setIsDiceRolling(false);
    setCombatState((prev) => ({...prev, phase: 'rolling'}));
    
    // Forcer le remontage du Canvas en changeant la clé (newKey déjà calculé plus haut)
    setDiceKey(newKey);
    
    // Réactiver le rolling après un court délai
    setTimeout(() => {
      setIsDiceRolling(true);
      console.log('🎮 [rollDiceWithSwipe] Canvas réactivé avec diceKey:', newKey);
    }, 10);
    
    console.log('🎮 [rollDiceWithSwipe] Dés lancés avec force et direction du geste !');
  }, [combatState.phase, diceKey, shakeAnim]);

  const rollDice = useCallback(() => {
    console.log('🎲🎲🎲 [rollDice] FONCTION APPELÉE !');
    console.log('🎲 [rollDice] Phase actuelle:', combatState.phase);
    
    if (combatState.phase !== 'idle' && combatState.phase !== 'results') {
      console.log('🎲 [rollDice] Lancer bloqué car phase =', combatState.phase);
      return;
    }

    // ⚠️ CRITIQUE : Forcer le remontage complet du Canvas en changeant diceKey AVANT de calculer le seed
    // Cela garantit que chaque lancer a un seed unique même si on clique rapidement
    setIsDiceRolling(false);
    const newKey = diceKey + 1;
    setDiceKey(newKey);
    
    // ⚠️ CRITIQUE : Réinitialiser le seed avec le temps ET le diceKey pour garantir l'unicité
    const seed = createSeedFromTimeAndForce(undefined, newKey);
    resetGlobalSeed(seed);
    console.log('🎲 [rollDice] Seed réinitialisé:', seed, 'diceKey:', newKey);

    // ⚠️ SIMPLIFICATION : Pour tester, on relance TOUJOURS la scène 3D complètement
    // Pas d'impulsion, on remonte tout depuis le début
    console.log('🎲 [rollDice] RELANCE COMPLÈTE - Remontage de la scène 3D');
    
    // Animation de shake légère
    Animated.sequence([
      Animated.timing(shakeAnim, {toValue: 5, duration: 40, useNativeDriver: true}),
      Animated.timing(shakeAnim, {toValue: -5, duration: 40, useNativeDriver: true}),
      Animated.timing(shakeAnim, {toValue: 0, duration: 40, useNativeDriver: true}),
    ]).start();

    setBattleResultMessage(null);
    setShowOutcomeMessage(false);
    setShowRerollOverlay(false);
    outcomeMessageAnim.setValue(0);
    rerollOverlayAnim.setValue(0);
    setDisplayedPlayerText('');
    setDisplayedEnemyText('');
    
    // ⚠️ CRITIQUE : Réinitialiser TOUS les états
    diceCompleteCalledRef.current = false;
    setSettledDiceResults({playerDice1: null, playerDice2: null, enemyDice1: null, enemyDice2: null});
    
    // ⚠️ CRITIQUE : Réinitialiser l'état de combat
    setCombatState({
      phase: 'rolling',
      playerDice1: null,
      playerDice2: null,
      enemyDice1: null,
      enemyDice2: null,
      playerTotal: 0,
      enemyTotal: 0,
    });
    
    // ⚠️ CRITIQUE : Réinitialiser les impulsions (vide pour forcer le remontage)
    setDiceImpulses({});
    
    // Réactiver après un court délai
    setTimeout(() => {
      setIsDiceRolling(true);
      console.log('🎲 [rollDice] Canvas réactivé avec diceKey:', newKey);
    }, 10);
    
    console.log('🎲 [rollDice] Scène 3D complètement réinitialisée');
  }, [combatState.phase, diceKey, shakeAnim]);

  const handleDiceComplete = useCallback(async (
    playerDice1: number,
    playerDice2: number,
    enemyDice1: number,
    enemyDice2: number
  ) => {
    console.log('🎯 [D6BattleWidget] ========== handleDiceComplete APPELÉ ==========');
    console.log('🎯 [D6BattleWidget] Résultats:', {playerDice1, playerDice2, enemyDice1, enemyDice2});
    console.log('🎯 [D6BattleWidget] Phase actuelle:', combatState.phase);
    
    // NE PAS arrêter isDiceRolling - on garde le Canvas visible avec les dés !
    console.log('🎯 [D6BattleWidget] Dés stabilisés, on garde le Canvas visible');

    // Mettre à jour l'état avec les vraies valeurs
    setCombatState((prev) => ({
      ...prev,
      playerDice1,
      playerDice2,
      enemyDice1,
      enemyDice2,
    }));
    
    // ⚠️ CRITIQUE : Marquer qu'on a déjà lancé au moins une fois
    // Cela permet de garder le Canvas visible même après réinitialisation
    setHasEverRolled(true);

    // Calculer totaux
    const playerTotal = playerDice1 + playerDice2 + playerForce + playerAttackBonus;
    const enemyTotal = enemyDice1 + enemyDice2 + enemyForce;

    console.log('[D6BattleWidget] Totaux calculés:', {playerTotal, enemyTotal});

    setCombatState((prev) => ({...prev, phase: 'results', playerTotal, enemyTotal}));
    setShowOutcomeMessage(true);
    
    // Réinitialiser les animations
    outcomeMessageAnim.setValue(0);
    rerollOverlayAnim.setValue(0);
    setShowRerollOverlay(false);
    
    // Générer les textes complets pour l'animation typewriter
    const playerText = `${playerDice1} + ${playerDice2} + ${playerForce}${playerAttackBonus !== 0 ? ` (${playerAttackBonus > 0 ? '+' : ''}${playerAttackBonus})` : ''} = ${playerTotal}`;
    const enemyText = `${enemyDice1} + ${enemyDice2} + ${enemyForce} = ${enemyTotal}`;
    
    // Réinitialiser les textes affichés
    setDisplayedPlayerText('');
    setDisplayedEnemyText('');
    
    // Animation typewriter rapide (les deux en parallèle)
    let playerIndex = 0;
    let enemyIndex = 0;
    const typewriterInterval = setInterval(() => {
      if (playerIndex < playerText.length) {
        setDisplayedPlayerText(playerText.substring(0, playerIndex + 1));
        playerIndex++;
      }
      if (enemyIndex < enemyText.length) {
        setDisplayedEnemyText(enemyText.substring(0, enemyIndex + 1));
        enemyIndex++;
      }
      if (playerIndex >= playerText.length && enemyIndex >= enemyText.length) {
        clearInterval(typewriterInterval);
      }
    }, 15); // Très rapide (15ms par caractère)

    // Animation d'impact
    Animated.sequence([
      Animated.timing(impactAnim, {toValue: 1, duration: 150, useNativeDriver: true}),
      Animated.timing(impactAnim, {toValue: 0, duration: 150, useNativeDriver: true}),
    ]).start();

    // Résoudre l'attaque
    await resolveAttack(playerDice1, playerDice2, enemyDice1, enemyDice2, playerTotal, enemyTotal);

    // Animation du message : zoom in puis zoom out + fade out
    Animated.sequence([
      // Zoom in (apparition)
      Animated.parallel([
        Animated.timing(outcomeMessageAnim, {
          toValue: 1,
          duration: 400,
          useNativeDriver: true,
        }),
      ]),
      // Maintenir visible pendant 2s pour que le joueur voie les résultats
      Animated.delay(2000),
      // Zoom out + fade out (disparition)
      Animated.parallel([
        Animated.timing(outcomeMessageAnim, {
          toValue: 2,
          duration: 300,
          useNativeDriver: true,
        }),
      ]),
    ]).start(() => {
      // ⚠️ CRITIQUE : Après avoir affiché les résultats et appliqué les dégâts,
      // réinitialiser automatiquement le tapis pour permettre un nouveau lancer
      // On reste sur le tapis, pas de sortie du modal
      console.log('🎯 [handleDiceComplete] Réinitialisation automatique du tapis après résultats');
      
      // Masquer les messages et overlay
      setShowOutcomeMessage(false);
      setShowRerollOverlay(false);
      setBattleResultMessage(null);
      
      // Réinitialiser les animations
      outcomeMessageAnim.setValue(0);
      rerollOverlayAnim.setValue(0);
      
      // Réinitialiser les textes affichés
      setDisplayedPlayerText('');
      setDisplayedEnemyText('');
      
      // Réinitialiser l'état des dés
      setSettledDiceResults({playerDice1: null, playerDice2: null, enemyDice1: null, enemyDice2: null});
      diceCompleteCalledRef.current = false;
      
      // Réinitialiser les impulsions des dés
      setDiceImpulses({});
      
      // ⚠️ CRITIQUE : Réinitialiser le tapis en phase 'idle' pour permettre un nouveau lancer
      // On reste sur le tapis, le Canvas reste visible mais prêt pour un nouveau lancer
      setCombatState({
        phase: 'idle',
        playerDice1: null,
        playerDice2: null,
        enemyDice1: null,
        enemyDice2: null,
        playerTotal: 0,
        enemyTotal: 0,
      });
      
      // Réinitialiser isDiceRolling pour que le Canvas soit prêt
      setIsDiceRolling(false);
      
      console.log('🎯 [handleDiceComplete] Tapis réinitialisé en phase idle, prêt pour un nouveau lancer');
    });

    diceCompleteCalledRef.current = false; // Réinitialiser pour permettre une nouvelle relance
  }, [playerForce, playerAttackBonus, enemyForce]);

  const resolveAttack = useCallback(
    async (
      playerDice1: number,
      playerDice2: number,
      enemyDice1: number,
      enemyDice2: number,
      playerTotal: number,
      enemyTotal: number
    ) => {
      if (!gameState) return;

      setCombatState((prev) => ({...prev, phase: 'resolving'}));

      try {
        // ⚠️ CRITIQUE : Lire DIRECTEMENT depuis le gameState au lieu des états locaux
        // Les états locaux peuvent être obsolètes après le premier coup
        // On lit toujours les valeurs actuelles depuis le gameState
        const enemyLifeField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_PV_MONSTER');
        const playerLifeField = GameStateHelper.getAdventureExtraField(gameState, 'D6_PV');
        const playerDamageField = GameStateHelper.getAdventureExtraField(gameState, 'D6_DAMAGE');
        const enemyDamageField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_DAMAGE_MONSTER');
        
        // Lire les valeurs actuelles depuis le gameState
        const currentEnemyLife = enemyLifeField ? parseValue(enemyLifeField.currentValue) ?? 0 : 0;
        const currentPlayerLife = playerLifeField ? parseValue(playerLifeField.currentValue) ?? 0 : 0;
        const currentPlayerDamage = playerDamageField ? parseValue(playerDamageField.currentValue) ?? 0 : 0;
        const currentEnemyDamage = enemyDamageField ? parseValue(enemyDamageField.currentValue) ?? 0 : 0;
        
        console.log('🎯 [resolveAttack] Valeurs lues depuis gameState:', {
          currentEnemyLife,
          currentPlayerLife,
          currentPlayerDamage,
          currentEnemyDamage,
        });
        
        let newEnemyLife = currentEnemyLife;
        let newPlayerLife = currentPlayerLife;
        let damageDealt = 0;

        if (playerTotal > enemyTotal) {
          // ⚠️ LE JOUEUR GAGNE - Seul l'ennemi perd des PV, le joueur NE PERD PAS
          damageDealt = currentPlayerDamage + playerDamageBonus;
          newEnemyLife = Math.max(0, currentEnemyLife - damageDealt);
          
          if (enemyLifeField) {
            // En combat, on ne peut que diminuer, jamais augmenter au-delà de initValue
            const maxLife = parseValue(enemyLifeField.initValue) ?? enemyMaxLife ?? newEnemyLife;
            // Si la nouvelle vie dépasse initValue, on la limite à initValue (ne devrait pas arriver en combat)
            newEnemyLife = Math.min(newEnemyLife, maxLife);
            
            // ⚠️ CRITIQUE : Utiliser updateGameStateStepExtraFieldValue pour mettre à jour SEULEMENT currentValue
            // Cette méthode modifie uniquement currentValue, pas initValue
            GameStateHelper.updateGameStateStepExtraFieldValue(gameState, enemyLifeField, newEnemyLife.toString());
            
            console.log('🎯 [resolveAttack] ✅ JOUEUR GAGNE - Ennemi perd', damageDealt, 'PV. Vie:', currentEnemyLife, '→', newEnemyLife, '/', maxLife);
          }

          setBattleResultMessage(newEnemyLife <= 0 
            ? `💀 L'adversaire est vaincu !` 
            : `L'adversaire perd ${damageDealt} PV`
          );

        } else if (enemyTotal > playerTotal) {
          // ⚠️ L'ENNEMI GAGNE - Seul le joueur perd des PV, l'ennemi NE PERD PAS
          damageDealt = currentEnemyDamage;
          newPlayerLife = Math.max(0, currentPlayerLife - damageDealt);
          
          if (playerLifeField) {
            // En combat, on ne peut que diminuer, jamais augmenter au-delà de initValue
            const maxLife = parseValue(playerLifeField.initValue) ?? playerMaxLife ?? newPlayerLife;
            // Si la nouvelle vie dépasse initValue, on la limite à initValue (ne devrait pas arriver en combat)
            newPlayerLife = Math.min(newPlayerLife, maxLife);
            
            // ⚠️ CRITIQUE : Utiliser updateAdventureExtraField pour mettre à jour correctement
            // On ne modifie QUE currentValue, pas initValue
            GameStateHelper.updateAdventureExtraField(gameState, 'D6_PV', undefined, newPlayerLife.toString());
            
            console.log('🎯 [resolveAttack] ⚠️ ENNEMI GAGNE - Joueur perd', damageDealt, 'PV. Vie:', currentPlayerLife, '→', newPlayerLife, '/', maxLife);
          }

          setBattleResultMessage(`Vous perdez ${damageDealt} PV`);

          if (newPlayerLife <= 0) {
            gameState.state = 'FAILED';
            setDefeatMessage('Vous n\'avez plus de points de vie !');
          }

        } else {
          // Égalité - personne ne perd de PV
          setBattleResultMessage('Parade ! Aucun dégât');
          console.log('🎯 [resolveAttack] ⚖️ ÉGALITÉ - Aucun dégât pour personne');
        }

        // ⚠️ CRITIQUE : Créer un nouvel objet gameState pour forcer la mise à jour React
        // ET sauvegarder en base de données
        const updatedGameState = {...gameState, data: {...gameState.data}};
        setGameState(updatedGameState);
        
        // ⚠️ CRITIQUE : Sauvegarder le gameState en base de données
        try {
          await gameStateService.updateGameState(updatedGameState);
          console.log('🎯 [resolveAttack] GameState sauvegardé en base de données');
        } catch (error) {
          console.error('🎯 [resolveAttack] Erreur lors de la sauvegarde du gameState:', error);
          // Ne pas bloquer l'UI si la sauvegarde échoue
        }
        
        // Le hook useLoadCombatValues se réexécutera automatiquement via le useEffect qui dépend de gameState
        setCombatState((prev) => ({...prev, phase: 'results'}));

      } catch (e) {
        console.error('[D6BattleWidget] Failed to resolve attack:', e);
      }
    },
    [gameState, step, playerDamageBonus, enemyMaxLife, playerMaxLife],
  );

  const getLifeColor = (percent: number): string => {
    if (percent >= 50) return '#4CAF50';
    if (percent >= 25) return '#FF9800';
    return '#F44336';
  };

  const hasRolled = combatState.playerDice1 !== null;
  const playerDice1 = combatState.playerDice1 ?? 1;
  const playerDice2 = combatState.playerDice2 ?? 1;
  const enemyDice1 = combatState.enemyDice1 ?? 1;
  const enemyDice2 = combatState.enemyDice2 ?? 1;

  const playerDiceSum = playerDice1 + playerDice2;
  const enemyDiceSum = enemyDice1 + enemyDice2;
  const playerTotal = combatState.playerTotal;
  const enemyTotal = combatState.enemyTotal;

  const playerWins = hasRolled && playerTotal > enemyTotal;
  const enemyWins = hasRolled && enemyTotal > playerTotal;
  const isDraw = hasRolled && playerTotal === enemyTotal;

  const playerLifePercent = playerMaxLife && playerMaxLife > 0 ? ((playerLife ?? 0) / playerMaxLife) * 100 : 100;
  const enemyLifePercent = enemyMaxLife && enemyMaxLife > 0 ? ((enemyLife ?? 0) / enemyMaxLife) * 100 : 100;
  
  // Fonction pour calculer la couleur du dégradé (vert à rouge selon le pourcentage perdu)
  const getHealthBarColor = (percent: number): string => {
    // percent = pourcentage de vie restante (0-100)
    // On veut : vert à 100%, rouge à 0%
    // Vert: #4CAF50 = rgb(76, 175, 80)
    // Rouge: #E74C3C = rgb(231, 76, 60)
    const r = Math.round(76 + (231 - 76) * (1 - percent / 100));
    const g = Math.round(175 + (76 - 175) * (1 - percent / 100));
    const b = Math.round(80 + (60 - 80) * (1 - percent / 100));
    return `rgb(${r}, ${g}, ${b})`;
  };

  const styles = createStyles(theme);

  // Composants 3D intégrés pour le tapis de jeu (JSX direct, pas de fonction wrapper)

  // État pour tracker les dés stabilisés
  const [settledDiceResults, setSettledDiceResults] = useState<{
    playerDice1: number | null;
    playerDice2: number | null;
    enemyDice1: number | null;
    enemyDice2: number | null;
  }>({playerDice1: null, playerDice2: null, enemyDice1: null, enemyDice2: null});

  // Ref pour savoir si on a déjà appelé handleDiceComplete
  const diceCompleteCalledRef = useRef(false);

  const handleDiceSettle = useCallback((diceKey: 'playerDice1' | 'playerDice2' | 'enemyDice1' | 'enemyDice2') => (face: number) => {
    console.log(`📍 [D6BattleWidget] Dé ${diceKey} stabilisé: ${face}`);
    
    // Protection : ne pas traiter si on a déjà appelé handleDiceComplete
    if (diceCompleteCalledRef.current) {
      console.log(`⚠️ [D6BattleWidget] Dé ${diceKey} ignoré car handleDiceComplete déjà appelé`);
      return;
    }
    
    setSettledDiceResults((prev) => {
      // Si ce dé a déjà été traité, ne pas mettre à jour
      if (prev[diceKey] !== null) {
        console.log(`⚠️ [D6BattleWidget] Dé ${diceKey} déjà traité (valeur: ${prev[diceKey]}), ignoré`);
        return prev;
      }
      
      const newState = {...prev, [diceKey]: face};
      console.log(`📍 [D6BattleWidget] Nouveau state après ${diceKey}:`, newState);
      
      // Vérifier si tous les dés sont maintenant stabilisés
      const {playerDice1, playerDice2, enemyDice1, enemyDice2} = newState;
      
      console.log(`🔍 [D6BattleWidget] Vérification complétude:`, {
        playerDice1,
        playerDice2,
        enemyDice1,
        enemyDice2,
        allSet: playerDice1 !== null && playerDice2 !== null && enemyDice1 !== null && enemyDice2 !== null,
        alreadyCalled: diceCompleteCalledRef.current
      });
      
      if (playerDice1 !== null && playerDice2 !== null && enemyDice1 !== null && enemyDice2 !== null && !diceCompleteCalledRef.current) {
        console.log('✅✅✅ [D6BattleWidget] TOUS LES DÉS STABILISÉS ! Callback dans 0.2s');
        console.log('✅ [D6BattleWidget] Résultats finaux:', newState);
        
        diceCompleteCalledRef.current = true;
        
        setTimeout(() => {
          // Double vérification avant d'appeler
          if (diceCompleteCalledRef.current) {
            console.log('🎯 [D6BattleWidget] Appel handleDiceComplete avec:', {playerDice1, playerDice2, enemyDice1, enemyDice2});
            handleDiceComplete(playerDice1, playerDice2, enemyDice1, enemyDice2);
          } else {
            console.warn('⚠️ [D6BattleWidget] handleDiceComplete annulé car diceCompleteCalledRef est false');
          }
        }, 200);
      } else if (playerDice1 !== null && playerDice2 !== null && enemyDice1 !== null && enemyDice2 !== null && diceCompleteCalledRef.current) {
        console.warn('⚠️ [D6BattleWidget] Tous les dés sont stabilisés mais handleDiceComplete déjà appelé');
      }
      
      return newState;
    });
  }, [handleDiceComplete]);

  // Positions et vélocités pour les 4 dés
  const diceInitialStates = React.useMemo(() => {
    // ⚠️ CRITIQUE : Récupérer le seed AVANT de l'utiliser pour garantir qu'on utilise le bon seed
    const rng = getGlobalSeededRandom();
    console.log('🎲 [diceInitialStates] Recalcul avec diceKey:', diceKey, 'seed utilisé depuis globalSeededRandom');
    
    // Récupérer les données du swipe depuis le ref (accès immédiat)
    const currentSwipe = swipeDataRef.current || {strength: 1, dirX: 0, dirZ: 0};
    const strength = currentSwipe.strength || 1;
    const swipeDirX = currentSwipe.dirX || 0;
    const swipeDirZ = currentSwipe.dirZ || 0;
    
    console.log('🎲 [diceInitialStates] Calcul avec swipe:', {strength, swipeDirX, swipeDirZ, hasSwipe: !!swipeDataRef.current, diceKey});
    
    // Nettoyer le ref après utilisation
    swipeDataRef.current = null;
    
    // Vélocité de base avec meilleure utilisation du geste pour un vrai jet de dés
    const baseVelMultiplier = strength * 1.2; // Augmenté pour plus d'énergie (0.6 à 3.6)
    // Influence du swipe plus importante pour la direction et la force
    const swipeInfluenceX = swipeDirX * 35; // Direction X du swipe (forte influence)
    const swipeInfluenceZ = swipeDirZ * 35; // Direction Z du swipe (forte influence)
    // Vélocité verticale initiale plus forte pour lancer plus haut
    const baseVerticalVel = -4.5 - (strength * 1.5); // Entre -4.5 et -7.5 selon la force
    
    // Positions initiales centrées et limitées pour que les dés restent dans l'écran
    // Zone visible: X entre -3.75 et 3.75, Z entre -3.75 et 3.75 (correspond aux murs)
    // Utiliser le seeded random pour des résultats cohérents mais variés
    
    return {
      playerDice1: {
        pos: [-0.6 + (rng.random() - 0.5) * 0.3, 5.5 + rng.random() * 0.5, -0.2 + (rng.random() - 0.5) * 0.3] as [number, number, number],
        vel: [((rng.random() - 0.5) * 2 + swipeInfluenceX * 0.5) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 2 + swipeInfluenceZ * 0.5) * baseVelMultiplier] as [number, number, number],
        ang: [(rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier] as [number, number, number],
      },
      playerDice2: {
        pos: [-0.3 + (rng.random() - 0.5) * 0.3, 5.5 + rng.random() * 0.5, -0.2 + (rng.random() - 0.5) * 0.3] as [number, number, number],
        vel: [((rng.random() - 0.5) * 2 + swipeInfluenceX * 0.5) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 2 + swipeInfluenceZ * 0.5) * baseVelMultiplier] as [number, number, number],
        ang: [(rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier] as [number, number, number],
      },
      enemyDice1: {
        pos: [0.3 + (rng.random() - 0.5) * 0.3, 5.5 + rng.random() * 0.5, 0.2 + (rng.random() - 0.5) * 0.3] as [number, number, number],
        vel: [((rng.random() - 0.5) * 2 + swipeInfluenceX * 0.5) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 2 + swipeInfluenceZ * 0.5) * baseVelMultiplier] as [number, number, number],
        ang: [(rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier] as [number, number, number],
      },
      enemyDice2: {
        pos: [0.6 + (rng.random() - 0.5) * 0.3, 5.5 + rng.random() * 0.5, 0.2 + (rng.random() - 0.5) * 0.3] as [number, number, number],
        vel: [((rng.random() - 0.5) * 2 + swipeInfluenceX * 0.5) * baseVelMultiplier, baseVerticalVel, ((rng.random() - 0.5) * 2 + swipeInfluenceZ * 0.5) * baseVelMultiplier] as [number, number, number],
        ang: [(rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier, (rng.random() - 0.5) * 35 * baseVelMultiplier] as [number, number, number],
      },
    };
  }, [diceKey]);

  // Écran de défaite
  if (playerLife !== null && playerLife <= 0 && defeatMessage) {
    return (
      <Animated.View style={[styles.defeatContainer, {opacity: fadeInAnim}]}>
        <FontAwesomeIcon icon={faSkull} size={80} color={theme.colors.text} style={{opacity: 0.3, marginBottom: 24}} />
        <Text style={styles.defeatTitle}>Défaite...</Text>
        <Text style={styles.defeatText}>{defeatMessage}</Text>
      </Animated.View>
    );
  }

  // État pour masquer le texte d'aide après première interaction
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Animation pour la main (mouvement de swipe)
  const handSwipeAnim = useRef(new Animated.Value(0)).current;
  const rerollHandAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    if (!hasInteracted && !isDiceRolling && combatState.phase === 'idle') {
      // Animation de swipe : mouvement diagonal de haut-gauche vers bas-droite
      const swipeAnimation = Animated.loop(
        Animated.sequence([
          Animated.parallel([
            Animated.timing(handSwipeAnim, {
              toValue: 1,
              duration: 1500,
              useNativeDriver: true,
            }),
          ]),
          Animated.timing(handSwipeAnim, {
            toValue: 0,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      swipeAnimation.start();
      return () => swipeAnimation.stop();
    }
  }, [hasInteracted, isDiceRolling, combatState.phase, handSwipeAnim]);
  
  // Animation pour le doigt du bouton "cliquer pour relancer"
  useEffect(() => {
    if (showRerollOverlay && combatState.phase === 'results') {
      // Même animation que pour le début : mouvement diagonal de haut-gauche vers bas-droite
      const rerollAnimation = Animated.loop(
        Animated.sequence([
          Animated.parallel([
            Animated.timing(rerollHandAnim, {
              toValue: 1,
              duration: 1500,
              useNativeDriver: true,
            }),
          ]),
          Animated.timing(rerollHandAnim, {
            toValue: 0,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      rerollAnimation.start();
      return () => rerollAnimation.stop();
    } else {
      rerollHandAnim.setValue(0);
    }
  }, [showRerollOverlay, combatState.phase, rerollHandAnim]);
  
  // Helper pour parser le texte avec style
  const renderTypewriterText = (text: string, diceStyle: any, isEnemy: boolean) => {
    if (!text) return <Text style={{opacity: 0.4}}>...</Text>;
    
    // Split par les nombres et garder les délimiteurs
    const parts = text.split(/(\d+)/);
    let beforeEquals = true;
    let diceCount = 0; // Compter combien de dés on a déjà vus
    
    return parts.map((part, index) => {
      if (/^\d+$/.test(part)) {
        // Si on a déjà vu "=", c'est le total final
        if (!beforeEquals) {
          return <Text key={index} style={{fontWeight: 'bold'}}>{part}</Text>;
        }
        // Colorer seulement les deux premiers nombres (les dés)
        diceCount++;
        if (diceCount <= 2) {
          return <Text key={index} style={diceStyle}>{part}</Text>;
        }
        // La force et le reste en couleur normale
        return <Text key={index} style={{color: theme.colors.text}}>{part}</Text>;
      }
      if (part.includes('=')) {
        beforeEquals = false;
        const parts = part.split('=');
        return (
          <React.Fragment key={index}>
            {parts[0]}
            {parts[1] && <Text style={{fontWeight: 'bold'}}>={parts[1]}</Text>}
          </React.Fragment>
        );
      }
      return <Text key={index}>{part}</Text>;
    });
  };
  
  // Interface de combat - REFONTE COMPLÈTE : Table de jeu
  const renderCombatInterface = () => (
    <Animated.View style={[styles.gameTableContainer, {opacity: fadeInAnim}]}>
      {/* SECTION ENNEMI - Compacte, en haut */}
      <View style={styles.enemySection}>
        {/* Barre de vie au-dessus du nom */}
        {enemyLife !== null && enemyMaxLife !== null && enemyMaxLife > 0 && (
          <View style={styles.healthBarAboveName}>
            <View 
              style={[
                styles.healthBarFill, 
                {
                  width: `${Math.min((enemyLife / enemyMaxLife) * 100, 100)}%`, 
                  backgroundColor: getHealthBarColor(enemyLifePercent)
                }
              ]} 
            />
          </View>
        )}
        <View style={styles.enemyHeader}>
          <FontAwesomeIcon icon={faSkull} size={20} color="#E74C3C" />
          <Text style={styles.enemyName}>Ennemi</Text>
          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <FontAwesomeIcon icon={faDumbbell} size={12} color="#E74C3C" />
              <Text style={styles.statText}>{enemyForce}</Text>
            </View>
            <View style={styles.statItem}>
              <FontAwesomeIcon icon={faBolt} size={12} color="#E74C3C" />
              <Text style={styles.statText}>{enemyDamage}</Text>
            </View>
            <View style={styles.statItem}>
              <FontAwesomeIcon icon={faHeart} size={12} color="#E74C3C" />
              <Text style={styles.statText}>
                {editorMode && enemyMaxLife !== null 
                  ? `${enemyLife ?? 0}/${enemyMaxLife}` 
                  : (enemyLife ?? 0)}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.combatResultRow}>
          {hasRolled ? (
            <>
              <FontAwesomeIcon icon={faDice} size={14} color="#E74C3C" style={{marginRight: 4}} />
              <Text style={styles.combatResultText}>
                {renderTypewriterText(displayedEnemyText, styles.diceValueEnemy, true)}
              </Text>
            </>
          ) : (
            <FontAwesomeIcon icon={faDice} size={17} color="#E74C3C" style={{opacity: 0.4}} />
          )}
        </View>
      </View>

      {/* ZONE DE LANCER - DOMINANTE, 35-40% hauteur */}
      <View
        style={styles.diceArena}
        {...((combatState.phase === 'idle' || combatState.phase === 'results') ? panResponder.panHandlers : {})}
      >
        {/* Indicateur de force pendant le swipe */}
        {swipeForce > 0 && (
          <Animated.View 
            style={[
              styles.swipeForceIndicator,
              {
                opacity: Math.min(swipeForce / 200, 1),
                transform: [
                  {scale: 0.5 + Math.min(swipeForce / 300, 0.5)},
                  {translateX: swipeDirection.x * 20},
                  {translateY: swipeDirection.y * 20},
                ],
              },
            ]}
          >
            <View style={[styles.swipeForceRing, {width: Math.min(swipeForce, 200), height: Math.min(swipeForce, 200)}]} />
            <Text style={styles.swipeForceText}>{Math.round(swipeForce)}</Text>
          </Animated.View>
        )}

        {/* Texte d'aide (disparaît après interaction) */}
        {!hasInteracted && !isDiceRolling && combatState.phase === 'idle' && (
          <Animated.View style={[styles.helpText, {opacity: fadeInAnim}]}>
            <Text style={styles.helpTextMain}>Glissez pour lancer</Text>
            <Text style={styles.helpTextSub}>ou tapez rapidement</Text>
          </Animated.View>
        )}

        {/* Résultats du combat (compact, intégré) */}
        {combatState.phase === 'results' && (
          <View style={styles.resultsCompact}>
            <View style={styles.resultsRow}>
              <View style={[styles.resultBadge, playerWins && styles.resultBadgeWin]}>
                <Text style={styles.resultLabel}>Héros</Text>
                <Text style={styles.resultValue}>{combatState.playerTotal}</Text>
              </View>
              <Text style={styles.resultVs}>VS</Text>
              <View style={[styles.resultBadge, enemyWins && styles.resultBadgeWin]}>
                <Text style={styles.resultLabel}>Ennemi</Text>
                <Text style={styles.resultValue}>{combatState.enemyTotal}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Canvas 3D - Vue top-down - toujours visible une fois qu'on a lancé */}
        <View 
          style={styles.diceCanvasWrapper} 
          key={diceKey}
          pointerEvents={(combatState.phase === 'idle' || combatState.phase === 'results') ? 'none' : 'auto'}
        >
          {/* Frame décorative HTML par-dessus le Canvas */}
          <View style={styles.decorativeFrameOverlay} pointerEvents="none">
            {/* Liseret fin à distance du bord (comme sur les vrais tapis) */}
            <View style={styles.frameBorderTop} />
            <View style={styles.frameBorderBottom} />
            <View style={styles.frameBorderLeft} />
            <View style={styles.frameBorderRight} />
          </View>
          
          <View style={styles.diceCanvasContainer} pointerEvents="none">
            <Canvas
              shadows
              camera={{position: [0, 8, 0], fov: 50}}
              gl={{antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.0}}
            >
              {/* Vue top-down : caméra regarde vers le bas */}
              <TopDownCamera />
              
              {/* Éclairage top-down (comme une lampe de table) */}
              <ambientLight intensity={0.7} color="#fff8f0" />
              <directionalLight
                position={[0, 5, 0]}
                intensity={2.0}
                castShadow
                shadow-mapSize={[2048, 2048]}
                shadow-camera-left={-3.5}
                shadow-camera-right={3.5}
                shadow-camera-top={3.5}
                shadow-camera-bottom={-3.5}
                shadow-bias={-0.0001}
                shadow-normalBias={0.02}
                color="#ffffff"
              />
              
              {/* Lumières d'accentuation pour les dés - adaptées à la nouvelle échelle */}
              <pointLight position={[-2, 3, 1.5]} intensity={0.8} color="#fff5e0" />
              <pointLight position={[2, 3, 1.5]} intensity={0.8} color="#fff5e0" />
              <pointLight position={[-2, 3, -1.5]} intensity={0.8} color="#fff5e0" />
              <pointLight position={[2, 3, -1.5]} intensity={0.8} color="#fff5e0" />
              
              {/* Tapis visible qui reçoit les ombres - toujours visible */}
              <VisibleTableMat />
              
              
              {/* Physique Rapier - toujours visible une fois qu'on a lancé au moins une fois */}
              {/* Le Canvas reste visible en phase 'idle' après réinitialisation pour permettre un nouveau lancer */}
              {(isDiceRolling || combatState.phase === 'results' || hasRolled || hasEverRolled) && (
                <Physics gravity={[0, -20, 0]}>
                  {/* Plateau invisible - sol solide qui couvre bien */}
                  <InvisibleTable />
                  
                  {/* Murs invisibles - physique uniquement, pas de rendu visuel */}
                  {/* Les murs correspondent EXACTEMENT aux limites visibles de la caméra (FOV 50, distance 8) */}
                  {/* Calcul: tan(25°) * 8 ≈ 3.73, mais on réduit un peu pour être sûr que les dés restent visibles */}
                  {/* Zone visible réelle ≈ 7.46 x 7.46, mais on met les murs à 3.5 pour avoir une marge */}
                  <RigidBody type="fixed">
                    {/* Murs gauche/droite - légèrement en retrait pour garder les dés visibles */}
                    <CuboidCollider args={[0.15, 12, 3.5]} position={[-3.5, 0, 0]} />
                    <CuboidCollider args={[0.15, 12, 3.5]} position={[3.5, 0, 0]} />
                    {/* Murs avant/arrière - légèrement en retrait */}
                    <CuboidCollider args={[3.5, 12, 0.15]} position={[0, 0, -3.5]} />
                    <CuboidCollider args={[3.5, 12, 0.15]} position={[0, 0, 3.5]} />
                    {/* Plafond pour empêcher les dés de sortir par le haut */}
                    <CuboidCollider args={[3.5, 0.15, 3.5]} position={[0, 7, 0]} />
                  </RigidBody>
                  
                  {/* Les 4 dés - seulement quand on lance (rolling) ou qu'on affiche les résultats (results) */}
                  {/* En phase 'idle', le tapis est vide et prêt pour un nouveau lancer */}
                  {(combatState.phase === 'rolling' || combatState.phase === 'results' || isDiceRolling) && (
                    <>
                      <PhysicsDice3D
                        color="#F5F5DC"
                        initialPosition={diceInitialStates.playerDice1.pos}
                        initialLinearVelocity={diceInitialStates.playerDice1.vel}
                        initialAngularVelocity={diceInitialStates.playerDice1.ang}
                        onSettle={handleDiceSettle('playerDice1')}
                        impulse={diceImpulses.playerDice1}
                        resetKey={impulseKey}
                      />
                      <PhysicsDice3D
                        color="#F5F5DC"
                        initialPosition={diceInitialStates.playerDice2.pos}
                        initialLinearVelocity={diceInitialStates.playerDice2.vel}
                        initialAngularVelocity={diceInitialStates.playerDice2.ang}
                        onSettle={handleDiceSettle('playerDice2')}
                        impulse={diceImpulses.playerDice2}
                        resetKey={impulseKey}
                      />
                      <PhysicsDice3D
                        color="#EF4444"
                        initialPosition={diceInitialStates.enemyDice1.pos}
                        initialLinearVelocity={diceInitialStates.enemyDice1.vel}
                        initialAngularVelocity={diceInitialStates.enemyDice1.ang}
                        onSettle={handleDiceSettle('enemyDice1')}
                        impulse={diceImpulses.enemyDice1}
                        resetKey={impulseKey}
                      />
                      <PhysicsDice3D
                        color="#EF4444"
                        initialPosition={diceInitialStates.enemyDice2.pos}
                        initialLinearVelocity={diceInitialStates.enemyDice2.vel}
                        initialAngularVelocity={diceInitialStates.enemyDice2.ang}
                        onSettle={handleDiceSettle('enemyDice2')}
                        impulse={diceImpulses.enemyDice2}
                        resetKey={impulseKey}
                      />
                    </>
                  )}
                </Physics>
              )}
            </Canvas>
          </View>
          
          {/* Indicateur de swipe - seulement en phase idle, au-dessus du Canvas */}
          {!hasInteracted && !isDiceRolling && combatState.phase === 'idle' && (
            <View style={styles.diceTablePlaceholder} pointerEvents="none">
              <Animated.View
                style={{
                  transform: [
                    {
                      translateX: handSwipeAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-60, 60],
                      }),
                    },
                    {
                      translateY: handSwipeAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-40, 40],
                      }),
                    },
                    {
                      rotate: handSwipeAnim.interpolate({
                        inputRange: [0, 0.5, 1],
                        outputRange: ['-15deg', '0deg', '15deg'],
                      }),
                    },
                  ],
                  opacity: handSwipeAnim.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.3, 0.6, 0.3],
                  }),
                }}
              >
                <FontAwesomeIcon icon={faHandPointUp} size={48} color={theme.colors.textLight} />
              </Animated.View>
            </View>
          )}
        </View>
        {/* Overlay "cliquer pour relancer" - visible en phase results avec fade-in */}
        {showRerollOverlay && combatState.phase === 'results' && (
          <Animated.View 
            style={[
              styles.rerollOverlay,
              {
                opacity: rerollOverlayAnim,
              },
            ]}
            pointerEvents="none"
          >
            <Animated.View
              style={[
                styles.rerollContent,
                {
                  transform: [
                    {
                      translateX: rerollHandAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-60, 60],
                      }),
                    },
                    {
                      translateY: rerollHandAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-40, 40],
                      }),
                    },
                    {
                      rotate: rerollHandAnim.interpolate({
                        inputRange: [0, 0.5, 1],
                        outputRange: ['-15deg', '0deg', '15deg'],
                      }),
                    },
                  ],
                  opacity: rerollHandAnim.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [0.3, 0.6, 0.3],
                  }),
                },
              ]}
            >
              <FontAwesomeIcon icon={faHandPointUp} size={48} color={theme.colors.textLight} />
            </Animated.View>
            <Animated.View style={[styles.helpText, {opacity: rerollOverlayAnim}]}>
              <Text style={styles.helpTextMain}>Glissez pour relancer</Text>
              <Text style={styles.helpTextSub}>ou tapez rapidement</Text>
            </Animated.View>
          </Animated.View>
        )}
        
        {/* View transparent pour gérer les interactions - au-dessus de tout */}
        {(combatState.phase === 'idle' || combatState.phase === 'results') && (
          <View 
            style={styles.diceArenaInteractionLayer}
            onTouchStart={() => !hasInteracted && setHasInteracted(true)}
            pointerEvents="box-none"
          />
        )}
      </View>

      {/* SECTION HÉROS - Compacte, en bas */}
      <View style={styles.heroSection}>
        {/* Barre de vie au-dessus du nom */}
        {playerLife !== null && playerMaxLife !== null && playerMaxLife > 0 && (
          <View style={styles.healthBarAboveName}>
            <View 
              style={[
                styles.healthBarFill, 
                {
                  width: `${Math.min((playerLife / playerMaxLife) * 100, 100)}%`, 
                  backgroundColor: getHealthBarColor(playerLifePercent)
                }
              ]} 
            />
          </View>
        )}
        <View style={styles.heroHeader}>
          <FontAwesomeIcon icon={faShield} size={20} color="#4CAF50" />
          <Text style={styles.heroName}>Héros</Text>
          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <FontAwesomeIcon icon={faDumbbell} size={12} color="#4CAF50" />
              <Text style={styles.statText}>{playerForce}</Text>
            </View>
            <View style={styles.statItem}>
              <FontAwesomeIcon icon={faBolt} size={12} color="#4CAF50" />
              <Text style={styles.statText}>{playerDamage}</Text>
            </View>
            <View style={styles.statItem}>
              <FontAwesomeIcon icon={faHeart} size={12} color="#4CAF50" />
              <Text style={styles.statText}>
                {editorMode && playerMaxLife !== null 
                  ? `${playerLife ?? 0}/${playerMaxLife}` 
                  : (playerLife ?? 0)}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.combatResultRow}>
          {hasRolled ? (
            <>
              <FontAwesomeIcon icon={faDice} size={14} color="#4CAF50" style={{marginRight: 4}} />
              <Text style={styles.combatResultText}>
                {renderTypewriterText(displayedPlayerText, styles.diceValuePlayer, false)}
              </Text>
            </>
          ) : (
            <FontAwesomeIcon icon={faDice} size={17} color="#4CAF50" style={{opacity: 0.4}} />
          )}
        </View>
      </View>


      {/* Messages d'issue (position fixe pour éviter le "saut") */}
      {showOutcomeMessage && (
        <Animated.View 
          style={[
            styles.outcomeMessage,
            {
              opacity: outcomeMessageAnim.interpolate({
                inputRange: [0, 1, 2],
                outputRange: [0, 1, 0],
              }),
              transform: [
                {
                  scale: outcomeMessageAnim.interpolate({
                    inputRange: [0, 1, 2],
                    outputRange: [0.3, 1, 0.3],
                  }),
                },
              ],
            },
          ]}
          pointerEvents="none"
        >
          {playerWins && (
            <View style={styles.outcomeRow}>
              <FontAwesomeIcon icon={faBolt} size={28} color="#4CAF50" style={{marginRight: 8}} />
              <Text style={[styles.outcomeText, styles.outcomeWin]}>TOUCHÉ !</Text>
            </View>
          )}
          {enemyWins && (
            <View style={styles.outcomeRow}>
              <FontAwesomeIcon icon={faShield} size={28} color="#E74C3C" style={{marginRight: 8}} />
              <Text style={[styles.outcomeText, styles.outcomeLose]}>COUP REÇU !</Text>
            </View>
          )}
          {isDraw && (
            <View style={styles.outcomeRow}>
              <FontAwesomeIcon icon={faBalanceScale} size={28} color="#FF9800" style={{marginRight: 8}} />
              <Text style={[styles.outcomeText, styles.outcomeDraw]}>PARADE !</Text>
            </View>
          )}
        </Animated.View>
      )}

      {battleResultMessage && (
        <View style={styles.damageMessage}>
          <Text style={styles.damageMessageText}>{battleResultMessage}</Text>
        </View>
      )}
    </Animated.View>
  );

  // Layout modal
  if (onFlee) {
    return (
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>⚔️ Combat ⚔️</Text>
          <TouchableOpacity onPress={onFlee} style={styles.fleeButton}>
            <Text style={styles.fleeButtonText}>
              {(enemyLife !== null && enemyLife <= 0) || (playerLife !== null && playerLife <= 0) ? 'Fermer' : 'Fuir'}
            </Text>
          </TouchableOpacity>
        </View>
        {renderCombatInterface()}
      </View>
    );
  }

  return renderCombatInterface();
};

const createStyles = (theme: any) => StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: theme.colors.bg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  fleeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: theme.colors.choiceBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  fleeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
  },
  gameTableContainer: {
    flex: 1,
    backgroundColor: theme.colors.bg,
  },
  enemySection: {
    padding: 12,
    backgroundColor: 'rgba(231, 76, 60, 0.05)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(231, 76, 60, 0.2)',
  },
  enemyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  enemyName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
    flex: 1,
  },
  enemyLife: {
    fontSize: 13,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  lifeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statText: {
    fontSize: 13,
    color: theme.colors.text,
    fontWeight: '600',
  },
  heroSection: {
    padding: 12,
    backgroundColor: 'rgba(76, 175, 80, 0.05)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 175, 80, 0.2)',
  },
  heroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  heroName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
    flex: 1,
  },
  heroLife: {
    fontSize: 13,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  combatResultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  combatResultText: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center',
  },
  diceValuePlayer: {
    fontWeight: 'bold',
    fontSize: 16,
    color: theme.colors.text,
  },
  diceValueEnemy: {
    fontWeight: 'bold',
    fontSize: 16,
    color: '#E74C3C',
  },
  diceArena: {
    flex: 1,
    minHeight: '35%',
    backgroundColor: '#2a1f15',
    marginHorizontal: -16, // Compenser le padding du conteneur parent
    marginVertical: 0,
    width: 'calc(100% + 32px)', // Web: compenser les marges négatives
    overflow: 'hidden',
    position: 'relative',
    ...Platform.select({
      web: {
        width: 'calc(100% + 32px)',
        marginLeft: -16,
        marginRight: -16,
      },
      default: {
        marginLeft: -16,
        marginRight: -16,
      },
    }),
  },
  diceArenaInteractionLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 10,
  },
  diceCanvasWrapper: {
    width: '100%',
    flex: 1,
    minHeight: 300, // Hauteur minimale pour éviter le saut
    position: 'relative',
  },
  decorativeFrameOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    pointerEvents: 'none',
  },
  // Liseret fin à distance du bord (comme sur les vrais tapis)
  // Distance du bord visible de l'écran : 12px
  // Épaisseur du liseret : 1px
  // Le conteneur parent (diceArena) a marginHorizontal: -16, donc on compense : 16 + 12 = 28px
  frameBorderTop: {
    position: 'absolute',
    top: 12,
    left: 28, // 16px (marge négative du parent) + 12px (marge souhaitée)
    right: 28,
    height: 1,
    backgroundColor: '#8b6f47',
    opacity: 0.6,
  },
  frameBorderBottom: {
    position: 'absolute',
    bottom: 12,
    left: 28,
    right: 28,
    height: 1,
    backgroundColor: '#8b6f47',
    opacity: 0.6,
  },
  frameBorderLeft: {
    position: 'absolute',
    top: 12,
    bottom: 12,
    left: 28,
    width: 1,
    backgroundColor: '#8b6f47',
    opacity: 0.6,
  },
  frameBorderRight: {
    position: 'absolute',
    top: 12,
    bottom: 12,
    right: 28,
    width: 1,
    backgroundColor: '#8b6f47',
    opacity: 0.6,
  },
  diceCanvasContainer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  diceTablePlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 60, // Décaler vers le bas pour ne pas être sous le texte
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  swipeForceIndicator: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginLeft: -100,
    marginTop: -100,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  swipeForceRing: {
    borderRadius: 1000,
    borderWidth: 3,
    borderColor: '#FFD700',
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
  },
  swipeForceText: {
    position: 'absolute',
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFD700',
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: {width: 0, height: 1},
    textShadowRadius: 3,
  },
  helpText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{translateX: -100}, {translateY: -30}],
    alignItems: 'center',
    zIndex: 5,
  },
  helpTextMain: {
    fontSize: 18,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: 4,
  },
  helpTextSub: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.5)',
  },
  resultsCompact: {
    position: 'absolute',
    top: 60,
    left: 0,
    right: 0,
    zIndex: 5,
  },
  resultsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  resultBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  resultBadgeWin: {
    backgroundColor: 'rgba(76, 175, 80, 0.3)',
    borderColor: '#4CAF50',
  },
  resultLabel: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
    marginBottom: 2,
  },
  resultValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  resultVs: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.5)',
    fontWeight: '600',
  },
  outcomeMessage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    pointerEvents: 'none',
  },
  healthBarAboveName: {
    height: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    marginBottom: 4,
    borderRadius: 1,
    overflow: 'hidden',
  },
  rerollOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 25,
    pointerEvents: 'box-none',
  },
  rerollContent: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  healthBarFill: {
    height: '100%',
    borderRadius: 1,
    transition: 'width 0.3s ease',
  },
  outcomeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  outcomeText: {
    fontSize: 32,
    fontWeight: 'bold',
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: {width: 0, height: 2},
    textShadowRadius: 4,
  },
  outcomeWin: {
    color: '#4CAF50',
  },
  outcomeLose: {
    color: '#E74C3C',
  },
  outcomeDraw: {
    color: '#FF9800',
  },
  damageMessage: {
    position: 'absolute',
    bottom: 100,
    left: 0,
    right: 0,
    padding: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    marginHorizontal: 20,
    borderRadius: 8,
    zIndex: 15,
  },
  damageMessageText: {
    fontSize: 14,
    color: '#ffffff',
    textAlign: 'center',
  },
  // Styles legacy (à nettoyer plus tard)
  container: {
    flex: 1,
    backgroundColor: theme.colors.bg,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
    paddingBottom: 100,
  },
  combatantCard: {
    backgroundColor: theme.colors.cardBg,
    borderRadius: 16,
    padding: 20,
    borderWidth: 2,
    borderColor: theme.colors.cardBorder,
    ...Platform.select({
      web: {boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'},
      default: {shadowColor: '#000', shadowOffset: {width: 0, height: 4}, shadowOpacity: 0.15, shadowRadius: 8, elevation: 6},
    }),
  },
  enemyCard: {
    borderTopWidth: 4,
    borderTopColor: '#E74C3C',
  },
  heroCard: {
    borderTopWidth: 4,
    borderTopColor: '#4CAF50',
  },
  cardOrnament: {
    position: 'absolute',
    top: 0,
    left: '10%',
    right: '10%',
    height: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    paddingTop: 8,
  },
  combatantName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text,
    flex: 1,
  },
  intentionBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255, 140, 66, 0.15)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FF8C42',
  },
  intentionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FF8C42',
  },
  healthSection: {
    marginBottom: 16,
  },
  healthHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  healthText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  healthBarContainer: {
    height: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  healthBarBg: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  healthBarFill: {
    height: '100%',
    borderRadius: 8,
  },
  healthBarShine: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  statChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  statForce: {
    backgroundColor: 'rgba(74, 144, 226, 0.1)',
    borderColor: '#4A90E2',
  },
  statDamage: {
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    borderColor: '#FF9800',
  },
  statBonus: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: '#4CAF50',
  },
  statLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  diceAreaInline: {
    marginTop: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    borderRadius: 12,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: theme.colors.borderLight,
    overflow: 'hidden',
  },
  diceInlineContent: {
    padding: 12,
    alignItems: 'center',
    gap: 8,
  },
  dice3DWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  diceInlineFallback: {
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'center',
  },
  diceBox: {
    width: 60,
    height: 60,
    backgroundColor: theme.colors.cardBg,
    borderRadius: 10,
    borderWidth: 3,
    borderColor: theme.colors.cardBorder,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)'},
      default: {shadowColor: '#000', shadowOffset: {width: 0, height: 3}, shadowOpacity: 0.15, shadowRadius: 3, elevation: 3},
    }),
  },
  diceBoxEnemy: {
    backgroundColor: '#FEE2E2',
    borderColor: '#DC2626',
  },
  diceValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  diceInlineResult: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textMuted,
    marginTop: 8,
  },
  resultTextOnly: {
    marginTop: 12,
    padding: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.borderLight,
  },
  resultFormula: {
    fontSize: 16,
    color: theme.colors.text,
    textAlign: 'center',
  },
  resultFormulaWin: {
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  diceTableSection: {
    marginVertical: 16,
    backgroundColor: theme.colors.cardBg,
    borderRadius: 16,
    padding: 16,
    borderWidth: 2,
    borderColor: theme.colors.cardBorder,
    ...Platform.select({
      web: {boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'},
      default: {shadowColor: '#000', shadowOffset: {width: 0, height: 4}, shadowOpacity: 0.15, shadowRadius: 8, elevation: 6},
    }),
  },
  diceTableTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
    textAlign: 'center',
    marginBottom: 12,
  },
  diceTableCanvas: {
    width: '100%',
    height: 400,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#1a1108',
  },
  resultsBanner: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  resultsBannerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 12,
  },
  resultsBannerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  resultBannerCard: {
    padding: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    alignItems: 'center',
    minWidth: 80,
  },
  resultBannerCardWinner: {
    backgroundColor: 'rgba(76, 175, 80, 0.3)',
    borderWidth: 2,
    borderColor: '#4CAF50',
  },
  resultBannerLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 4,
  },
  resultBannerDice: {
    fontSize: 14,
    color: '#ffffff',
    marginTop: 4,
  },
  resultBannerTotal: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 4,
  },
  resultBannerVs: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'rgba(255, 255, 255, 0.5)',
  },
  resultsBannerHint: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center',
    marginTop: 12,
  },
  centralMessage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{translateX: -80}, {translateY: -20}],
    zIndex: 20,
  },
  centralMessageText: {
    fontSize: 32,
    fontWeight: 'bold',
    textShadowColor: 'rgba(0, 0, 0, 0.8)',
    textShadowOffset: {width: 0, height: 2},
    textShadowRadius: 4,
  },
  swipeIndicator: {
    position: 'absolute',
    top: 20,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: 8,
    borderRadius: 8,
  },
  swipeIndicatorText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
});

const D6BattleWidget = React.memo(D6BattleWidgetComponent);

export default D6BattleWidget;
